import React from "react";
import { createPortal } from 'react-dom';
import { Heart, X, Link, Eye, MessageCircle, ChevronLeft, ChevronRight, Download, Send } from 'lucide-react';

class PhotoModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLiked: props.photo.is_liked,
      scale: 1,
      transformOrigin: '50% 50%',
      isMobile: window.innerWidth < 1024,
      offsetX: 0,
      offsetY: 0,
      lastTouchDistance: 0,
      lastTouchX: 0,
      lastTouchY: 0,
      isSyncing: false,
      isSynced: false
    };

    this.imageRef = React.createRef();
    this.handleResize = this.handleResize.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('resize', this.handleResize);
    if (this.state.isMobile) {
      this.imageRef.current.addEventListener('touchstart', this.handleTouchStart);
      this.imageRef.current.addEventListener('touchmove', this.handleTouchMove);
      this.imageRef.current.addEventListener('touchend', this.handleTouchEnd);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleResize);
    if (this.state.isMobile) {
      this.imageRef.current.removeEventListener('touchstart', this.handleTouchStart);
      this.imageRef.current.removeEventListener('touchmove', this.handleTouchMove);
      this.imageRef.current.removeEventListener('touchend', this.handleTouchEnd);
    }
  }

  handleResize() {
    this.setState({
      isMobile: window.innerWidth < 1024,
      scale: 1, // Reset scale on resize
      offsetX: 0,
      offsetY: 0
    });
  }

  handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.props.onClose();
    } else if (event.key === 'ArrowLeft') {
      this.props.onNavigate(-1);
    } else if (event.key === 'ArrowRight') {
      this.props.onNavigate(1);
    }
  }

  handleModalClick = (e) => {
    const isClickableElement = e.target.closest('a, button, img, .sidebar-content, .mobile-content');
    if (!isClickableElement) {
      this.props.onClose();
    }
  }

  handleDoubleTap = (event) => {
    event.preventDefault();
    if (this.state.scale > 1) {
      this.setState({
        scale: 1,
        transformOrigin: '50% 50%',
        offsetX: 0,
        offsetY: 0
      });
    } else {
      const rect = event.target.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      this.setState({
        scale: 2,
        transformOrigin: `${x}% ${y}%`
      });
    }
  }

  // Touch support for zooming and panning
  handleTouchStart(event) {
    if (event.touches.length === 1) {
      this.setState({
        lastTouchX: event.touches[0].clientX,
        lastTouchY: event.touches[0].clientY
      });
    } else if (event.touches.length === 2) {
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      this.setState({
        lastTouchDistance: Math.sqrt(dx * dx + dy * dy)
      });
    }
  }

  handleTouchMove(event) {
    if (event.touches.length === 1 && this.state.scale > 1) {
      const touch = event.touches[0];
      const dx = touch.clientX - this.state.lastTouchX;
      const dy = touch.clientY - this.state.lastTouchY;
  
      // Use requestAnimationFrame for smoother animations
      requestAnimationFrame(() => {
        this.setState(prevState => ({
          offsetX: prevState.offsetX + dx,
          offsetY: prevState.offsetY + dy,
          lastTouchX: touch.clientX,
          lastTouchY: touch.clientY
        }));
      });
    } else if (event.touches.length === 2) {
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const scale = distance / this.state.lastTouchDistance;
      this.setState(prevState => ({
        scale: Math.max(1, Math.min(prevState.scale * scale, 5)),
        lastTouchDistance: distance
      }));
    }
  }

  handleTouchEnd(event) {
    // Reset touch data
    this.setState({
      lastTouchDistance: 0
    });
  }

  handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const csrfToken = document.querySelector('[name=csrf-token]');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken.content;
      }
      
      const response = await fetch('/like', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          media_item: { id: this.props.photo.id }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        this.setState({ isLiked: data.liked });
      }
    } catch (error) {
      console.error('Error liking photo:', error);
    }
  }

  handleDownload = async () => {
    try {
      const response = await fetch(this.getFinalUrl());
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const date = new Date(this.props.photo.taken_at * 1000);
      const fileName = `${this.props.account.nickname}_${date.toISOString().split('T')[0]}.jpg`;
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  }

  handleSync = async () => {
    if (this.state.isSyncing) return;
    
    this.setState({ isSyncing: true });
    try {
      const csrfToken = document.querySelector('[name=csrf-token]');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken.content;
      }
      
      const response = await fetch(`/media_items/${this.props.photo.id}/sync`, {
        method: 'POST',
        headers: headers
      });
      
      if (response.ok) {
        this.setState({ isSynced: true, isSyncing: false });
        setTimeout(() => this.setState({ isSynced: false }), 3000);
      } else {
        const errData = await response.json();
        alert(`Ошибка при отправке: ${errData.error || 'Неизвестная ошибка'}`);
        this.setState({ isSyncing: false });
      }
    } catch (error) {
      console.error('Error syncing photo to X6:', error);
      alert('Ошибка при отправке в X6.');
      this.setState({ isSyncing: false });
    }
  }

  formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  generateColorFromName(name) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 25%)`;
  }

  getInitial(nickname) {
    return nickname ? nickname.charAt(0).toUpperCase() : '?';
  }

  getFinalUrl() {
    const { image, display_url } = this.props.photo;
    return (image && image.url) ? image.url : display_url;
  }

  renderDesktopLayout() {
    const { photo, account, onNavigate } = this.props;
    const { isLiked, scale, transformOrigin } = this.state;
    const finalUrl = this.getFinalUrl();

    const imageStyle = {
      transform: `scale(${scale})`,
      transformOrigin,
      transition: 'transform 0.3s ease-out'
    };

    return (
      <div className="flex items-center w-full justify-between">
        <button
          onClick={() => onNavigate(-1)}
          className="flex-shrink-0 mx-4 text-white/50 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>

        <div className="flex flex-1 h-[90vh] min-w-0 max-w-[80%] justify-center">
          <div className="h-full flex items-center">
            <img 
              src={finalUrl} 
              className="max-h-full max-w-full object-contain select-none"
              alt={photo.description || ''}
              onDoubleClick={this.handleDoubleTap}
              style={imageStyle}
              draggable="false"
            />
          </div>

          <div className="w-[400px] bg-gray-900 overflow-hidden flex flex-col shadow-xl sidebar-content">
            {this.renderContent()}
          </div>
        </div>

        <button
          onClick={() => onNavigate(1)}
          className="flex-shrink-0 mx-4 text-white/50 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      </div>
    );
  }

  renderMobileLayout() {
    const { photo, onNavigate } = this.props;
    const { scale, transformOrigin, offsetX, offsetY } = this.state;
    const finalUrl = this.getFinalUrl();

    const imageStyle = {
      transform: `scale(${scale}) translate(${offsetX}px, ${offsetY}px)`,
      transformOrigin,
      transition: 'transform 0.3s ease-out'
    };

    return (
      <div className="flex flex-col h-full">
        <div className="relative h-[50vh] bg-black flex items-center justify-center">
          <img 
            ref={this.imageRef}
            src={finalUrl} 
            className="max-h-full max-w-full object-contain select-none"
            alt={photo.description || ''}
            onDoubleClick={this.handleDoubleTap}
            style={imageStyle}
            draggable="false"
          />
          
          <div className="absolute bottom-4 left-0 right-0 flex justify-between px-4">
            <button
              onClick={() => onNavigate(-1)}
              className="p-2 bg-black/50 backdrop-blur-sm rounded-full text-white/80 hover:text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => onNavigate(1)}
              className="p-2 bg-black/50 backdrop-blur-sm rounded-full text-white/80 hover:text-white"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mobile-content bg-gray-900">
          {this.renderContent()}
        </div>
      </div>
    );
  }


  renderContent() {
    const { photo, account } = this.props;
    const { isLiked } = this.state;

    return (
      <>
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center space-x-3 mb-4">
            {account.avatar && account.avatar.url ? (
              <img
                src={account.avatar.url}
                alt={account.nickname}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium text-white"
                style={{ backgroundColor: this.generateColorFromName(account.nickname) }}
              >
                {this.getInitial(account.nickname)}
              </div>
            )}
            <div>
              <a 
                href={`https://instagram.com/${account.nickname}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium hover:underline"
              >
                {account.nickname}
              </a>
            </div>
          </div>

          <div className="flex gap-2">
            <a 
              href={`https://instagram.com/p/${photo.shortcode}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-between px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all"
            >
              <span className="text-sm font-medium">Открыть в Instagram</span>
              <Link className="w-4 h-4" />
            </a>
            
            <button
              onClick={this.handleDownload}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-all"
            >
              <span className="text-sm font-medium">Скачать</span>
              <Download className="w-4 h-4" />
            </button>
          </div>
          
          <div className="mt-2">
            <button
              onClick={this.handleSync}
              disabled={this.state.isSyncing}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-all ${
                this.state.isSynced 
                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                  : 'bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50'
              }`}
            >
              <span className="text-sm font-medium">
                {this.state.isSyncing ? 'Отправка...' : this.state.isSynced ? 'Отправлено в X6!' : 'Отправить в X6'}
              </span>
              <Send className="w-4 h-4" />
            </button>
          </div>
          
          <div className="mt-3 text-sm text-gray-400">
            {this.formatDate(photo.taken_at)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 scroll-smooth">
          {photo.description && (
            <p className="text-sm text-gray-100 whitespace-pre-wrap leading-relaxed">
              {photo.description}
            </p>
          )}
        </div>

        <div className="border-t border-gray-800 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Heart className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium">{photo.likes}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium">{photo.comments}</span>
              </div>
              {photo.views_count && (
                <div className="flex items-center space-x-2">
                  <Eye className="w-5 h-5 text-gray-500" />
                  <span className="text-sm font-medium">{photo.views_count}</span>
                </div>
              )}
            </div>

            <button 
              onClick={this.handleLike}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              aria-label={isLiked ? "Unlike" : "Like"}
            >
              <Heart 
                className={`w-5 h-5 ${isLiked ? 'fill-rose-500 text-rose-500' : 'text-gray-500'}`}
              />
              <span className="text-sm">В избранное</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  renderKeyboardHints() {
    if (this.state.isMobile) {
      return (
        <div>
        </div>
      );
    }

    return (
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center space-x-6 text-white/30">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <kbd className="px-2 py-1 bg-white/10 rounded text-xs">←</kbd>
            <kbd className="px-2 py-1 bg-white/10 rounded text-xs">→</kbd>
          </div>
          <span className="text-xs">листать фото</span>
        </div>
        <div className="flex items-center space-x-2">
          <kbd className="px-2 py-1 bg-white/10 rounded text-xs">Esc</kbd>
          <span className="text-xs">закрыть</span>
        </div>
      </div>
    );
  }

  render() {
    return createPortal(
      <div 
        className="fixed inset-0 z-[9999] overflow-hidden bg-black/95"
        onClick={this.handleModalClick}
      >
        <div className="w-full h-full flex items-center justify-center relative">
          <button 
            onClick={this.props.onClose}
            className="absolute top-4 right-4 z-[10001] p-2 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {this.state.isMobile ? this.renderMobileLayout() : this.renderDesktopLayout()}
          {this.renderKeyboardHints()}
        </div>
      </div>,
      document.body
    );
  }
}

export default PhotoModal;